// src/components/VoterList.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import {
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from './LoadingSpinner';

const VoterList = ({ proposalId }) => {
  const [votes, setVotes] = useState([]);
  const [voterProfiles, setVoterProfiles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredVotes, setFilteredVotes] = useState([]);

  // 投票データを取得
  useEffect(() => {
    const fetchVotes = async () => {
      try {
        setIsLoading(true);

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/votes/proposal/${proposalId}`
        );
        setVotes(response.data);
        setFilteredVotes(response.data);

        setIsLoading(false);

        // ユーザープロファイルの取得
        await fetchVoterProfiles(response.data);
      } catch (error) {
        console.error('投票取得エラー:', error);
        setError('投票データの取得に失敗しました');
        setIsLoading(false);
      }
    };

    fetchVotes();
  }, [proposalId]);

  // 投票者のプロフィール情報を取得
  const fetchVoterProfiles = async votesData => {
    try {
      setIsLoadingProfiles(true);

      // 投票者のアドレス一覧を取得（重複を削除）
      const voterAddresses = [...new Set(votesData.map(vote => vote.voterAddress))];

      // 一度に全てのプロフィールを取得するのは非効率なので、バッチ処理
      const batchSize = 10;
      const profiles = {};

      for (let i = 0; i < voterAddresses.length; i += batchSize) {
        const batch = voterAddresses.slice(i, i + batchSize);

        // 並列でプロフィール取得
        const profilePromises = batch.map(address =>
          axios
            .get(`${process.env.REACT_APP_API_URL}/api/users/profile/${address}`)
            .then(res => ({ address, profile: res.data }))
            .catch(err => {
              console.error(`Error fetching profile for ${address}:`, err);
              return { address, profile: null };
            })
        );

        const results = await Promise.all(profilePromises);

        // 結果をマージ
        results.forEach(({ address, profile }) => {
          if (profile) {
            profiles[address] = profile;
          }
        });
      }

      setVoterProfiles(profiles);
      setIsLoadingProfiles(false);
    } catch (error) {
      console.error('プロフィール取得エラー:', error);
      setIsLoadingProfiles(false);
    }
  };

  // 検索フィルタの適用
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredVotes(votes);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = votes.filter(vote => {
      const voterAddress = vote.voterAddress;
      const choiceText = vote.choice.text;

      // プロフィール情報を考慮した検索
      const profile = voterProfiles[vote.voterAddress];
      const displayName = profile?.displayName?.toLowerCase() || '';

      return (
        voterAddress.includes(query) || choiceText.includes(query) || displayName.includes(query)
      );
    });

    setFilteredVotes(filtered);
  }, [votes, searchQuery, voterProfiles]);

  // アドレスを短縮
  const shortenAddress = address => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // ユーザー名を表示（あれば）
  const displayUserName = address => {
    const profile = voterProfiles[address];
    if (profile && profile.displayName) {
      return (
        <div className="flex flex-col">
          <span className="font-medium">{profile.displayName}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {shortenAddress(address)}
          </span>
        </div>
      );
    }
    return shortenAddress(address);
  };

  // ブロックエクスプローラーのリンクを生成
  const getExplorerLink = address => {
    return `${process.env.REACT_APP_BLOCK_EXPLORER_URL}/address/${address}`;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-4">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded my-2">
        {error}
      </div>
    );
  }

  if (!votes || votes.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 dark:text-gray-400">まだ投票がありません</div>
    );
  }

  return (
    <div className="mt-4">
      {/* 検索ボックス */}
      <div className="mb-4 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="block w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="アドレスや名前、選択肢で検索..."
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* 投票者リスト */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                投票者
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                選択肢
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                投票の重み
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                投票日時
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredVotes.map(vote => (
              <tr key={vote.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-4 py-2 whitespace-nowrap">
                  <div className="flex items-center">
                    <Link
                      to={`/user/${vote.voterAddress}`}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mr-1"
                    >
                      {displayUserName(vote.voterAddress)}
                    </Link>
                    <a
                      href={getExplorerLink(vote.voterAddress)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title="ブロックエクスプローラーで表示"
                      onClick={e => e.stopPropagation()}
                    >
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">{vote.choice.text}</td>
                <td className="px-4 py-2 whitespace-nowrap text-right font-medium">
                  {vote.weight}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-right text-xs text-gray-500 dark:text-gray-400">
                  {format(new Date(vote.createdAt), 'yyyy/MM/dd HH:mm', { locale: ja })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 検索結果情報 */}
      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        {filteredVotes.length} / {votes.length} 件の投票を表示
        {searchQuery && <span> (検索フィルター適用中)</span>}
      </div>

      {isLoadingProfiles && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center">
          <LoadingSpinner size="sm" />
          <span className="ml-2">ユーザープロフィール読み込み中...</span>
        </div>
      )}
    </div>
  );
};

export default VoterList;
